import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VELA_APPROVALS,
  VELA_DONE_THIS_WEEK,
  type VelaApprovalKind,
} from '../../vela-inbox/data';

const VELA_GRADIENT = 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 100%)';

const EDGE: Record<VelaApprovalKind, string> = {
  urgent: '#DC2626',
  good: '#0F6E56',
  warn: '#CA8A04',
};

const TAG_STYLE: Record<VelaApprovalKind, { background: string; color: string }> = {
  urgent: { background: '#FEE2E2', color: '#DC2626' },
  good:   { background: '#DCFCE7', color: '#0F6E56' },
  warn:   { background: '#FEF3C7', color: '#CA8A04' },
};

type Filter = 'all' | 'urgent' | 'good' | 'warn' | 'done-week' | 'snoozed';

/**
 * Full Vela inbox at /vela/inbox.
 * Left-rail filters (All / Urgent / Payroll + good / Cash + warn / Done this week / Snoozed)
 * and right-side cards. Reachable from the dashboard's Vela banner "Open Vela inbox" link.
 */
export default function VelaInboxPage(): React.ReactElement {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');

  const counts = {
    all: VELA_APPROVALS.length,
    urgent: VELA_APPROVALS.filter((a) => a.kind === 'urgent').length,
    good: VELA_APPROVALS.filter((a) => a.kind === 'good').length,
    warn: VELA_APPROVALS.filter((a) => a.kind === 'warn').length,
    doneWeek: VELA_DONE_THIS_WEEK.length,
    snoozed: 0,
  };

  const approvalsForView =
    filter === 'all'
      ? VELA_APPROVALS
      : filter === 'urgent' || filter === 'good' || filter === 'warn'
      ? VELA_APPROVALS.filter((a) => a.kind === filter)
      : [];

  const doneForView = filter === 'done-week' ? VELA_DONE_THIS_WEEK : [];

  const F = ({ id, label, count }: { id: Filter; label: string; count: number }) => {
    const active = filter === id;
    return (
      <div
        onClick={() => setFilter(id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFilter(id); }}
        style={{
          padding: active ? '8px 16px 8px 13px' : '8px 16px',
          cursor: 'pointer',
          fontSize: 13,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: active ? '#F0FDF8' : 'transparent',
          color: active ? '#0F6E56' : '#0E1A1A',
          fontWeight: active ? 700 : 500,
          borderLeft: active ? '3px solid #15A08C' : '3px solid transparent',
        }}
      >
        {label}
        <span style={{
          background: active ? '#15A08C' : '#F1F5F9',
          color: active ? '#fff' : '#66748B',
          fontSize: 10, fontWeight: 700,
          padding: '1px 7px', borderRadius: 999,
        }}>{count}</span>
      </div>
    );
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", maxWidth: '100%', margin: 0, padding: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', margin: 0, letterSpacing: '-0.01em' }}>Vela inbox</h2>
          <p style={{ fontSize: 12, color: '#66748B', margin: '4px 0 0' }}>
            Everything Vela drafted or wants to do. Nothing happens until you review and confirm.
          </p>
        </div>
        <button
          onClick={() => navigate('/agents')}
          style={{
            background: VELA_GRADIENT, color: '#fff',
            border: 0, padding: '10px 16px',
            borderRadius: 10, fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(107,70,193,0.25)',
          }}
        >Chat with Vela &rsaquo;</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 18 }}>
        <div style={{ background: '#fff', border: '1px solid #E7EAF0', borderRadius: 12, padding: '14px 0', height: 'fit-content' }}>
          <h4 style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#66748B', margin: '0 16px 10px', fontWeight: 800 }}>
            Needs review
          </h4>
          <F id="all" label="All" count={counts.all}/>
          <F id="urgent" label="Urgent" count={counts.urgent}/>
          <F id="good" label="Payroll + good" count={counts.good}/>
          <F id="warn" label="Cash + warnings" count={counts.warn}/>
          <h4 style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#66748B', margin: '14px 16px 10px', fontWeight: 800 }}>
            Done
          </h4>
          <F id="done-week" label="This week" count={counts.doneWeek}/>
          <h4 style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#66748B', margin: '14px 16px 10px', fontWeight: 800 }}>
            Dismissed
          </h4>
          <F id="snoozed" label="Snoozed" count={counts.snoozed}/>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {approvalsForView.length === 0 && doneForView.length === 0 && (
            <div style={{
              background: '#fff', border: '1px solid #E7EAF0', borderRadius: 12,
              padding: 40, textAlign: 'center',
              color: '#94A3B8', fontSize: 13, fontStyle: 'italic',
            }}>
              Nothing here. Vela will put things in your inbox as it works.
            </div>
          )}

          {approvalsForView.map((a) => (
            <div key={a.id} style={{
              background: '#fff', border: '1px solid #E7EAF0', borderRadius: 12,
              padding: '14px 16px',
              display: 'grid', gridTemplateColumns: '1fr 180px', gap: 14,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: 3, background: EDGE[a.kind],
              }}/>
              <div>
                <span style={{
                  display: 'inline-block',
                  fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  padding: '2px 7px', borderRadius: 999,
                  marginBottom: 6,
                  ...TAG_STYLE[a.kind],
                }}>{a.tag}</span>
                <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4, marginBottom: 6, color: '#0E1A1A' }}>
                  {a.title}
                </div>
                <div style={{ fontSize: 11, color: '#66748B', fontStyle: 'italic' }}>
                  {a.hint}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                <button
                  onClick={() => navigate(a.reviewHref)}
                  style={{
                    background: '#0E1A1A', color: '#fff',
                    border: 0, padding: '10px 16px',
                    borderRadius: 10, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(14,26,26,0.1)',
                  }}
                >{a.btnLabel} &rsaquo;</button>
                {a.skipLabel && (
                  <button style={{
                    background: '#fff', color: '#0E1A1A',
                    border: '1px solid #E7EAF0', padding: '10px 16px',
                    borderRadius: 10, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                  }}>{a.skipLabel}</button>
                )}
              </div>
            </div>
          ))}

          {doneForView.map((d) => (
            <div key={d.id} style={{
              background: '#FAFBFC', border: '1px solid #E7EAF0', borderRadius: 12,
              padding: '14px 16px',
              display: 'grid', gridTemplateColumns: '1fr 140px', gap: 14,
              position: 'relative', overflow: 'hidden',
              opacity: 0.75,
            }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0,
                width: 3, background: '#94A3B8',
              }}/>
              <div>
                <span style={{
                  display: 'inline-block',
                  fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                  padding: '2px 7px', borderRadius: 999,
                  marginBottom: 6,
                  background: '#F1F5F9', color: '#475569',
                }}>{d.tag}</span>
                <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: '#0E1A1A' }}>
                  {d.title}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <button style={{
                  background: '#fff', color: '#0E1A1A',
                  border: '1px solid #E7EAF0', padding: '10px 16px',
                  borderRadius: 10, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                }}>View &rsaquo;</button>
                <div style={{ fontSize: 10, color: '#66748B' }}>{d.meta}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
